import React from 'react';
import { DollarSign, TrendingUp, CreditCard, Landmark, PieChart } from 'lucide-react';

/**
 * Detailed Profitability Tables Component
 * Shows line-by-line breakdown mirroring the gross profitability tables
 * - CC table: Non-Amex, Debit, Amex lines
 * - ACH table: Single ACH line  
 * - Combined: All payment types with proper commission calculation (CC only)
 * 
 * Supports aggregation across multiple services/scenarios
 */
export default function ProfitabilityTablesDetailed({ profitabilityResult, scenarios, loading = false }) {
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-64 bg-gray-100 animate-pulse rounded-lg"></div>
        <div className="h-64 bg-gray-100 animate-pulse rounded-lg"></div>
        <div className="h-64 bg-gray-100 animate-pulse rounded-lg"></div>
      </div>
    );
  }

  // If multiple scenarios provided, aggregate their data
  let aggregatedData = profitabilityResult;
  
  if (scenarios && scenarios.length > 1) {
    // Aggregate payment types across all scenarios
    const aggregatedPaymentTypes = {
      CC: { total_fees: 0, merchant_fees: 0, net_revenue: 0, total_collections: 0, total_transactions: 0, total_payments: 0, total_payments_midpoint: 0, implied_tr: 0 },
      AMEX: { total_fees: 0, merchant_fees: 0, net_revenue: 0, total_collections: 0, total_transactions: 0, total_payments: 0, total_payments_midpoint: 0, implied_tr: 0 },
      DEBIT: { total_fees: 0, merchant_fees: 0, net_revenue: 0, total_collections: 0, total_transactions: 0, total_payments: 0, total_payments_midpoint: 0, implied_tr: 0 },
      ACH: { total_fees: 0, merchant_fees: 0, net_revenue: 0, total_collections: 0, total_transactions: 0, total_payments: 0, total_payments_midpoint: 0, implied_tr: 0 }
    };
    
    scenarios.forEach(scenario => {
      const prof = scenario.profitability_results || {};
      const paymentTypes = prof.payment_types || {};
      
      Object.keys(aggregatedPaymentTypes).forEach(type => {
        const pt = paymentTypes[type] || {};
        aggregatedPaymentTypes[type].total_fees += pt.total_fees || 0;
        aggregatedPaymentTypes[type].merchant_fees += pt.merchant_fees || 0;
        aggregatedPaymentTypes[type].net_revenue += pt.net_revenue || 0;
        aggregatedPaymentTypes[type].total_collections += pt.total_collections || 0;
        aggregatedPaymentTypes[type].total_transactions += pt.total_transactions || 0;
        aggregatedPaymentTypes[type].total_payments += pt.total_payments || 0;
        aggregatedPaymentTypes[type].total_payments_midpoint += pt.total_payments_midpoint || pt.total_payments || 0;
      });
    });
    
    // Recalculate percentages for aggregated data using midpoint-based payments for implied TR
    Object.keys(aggregatedPaymentTypes).forEach(type => {
      const pt = aggregatedPaymentTypes[type];
      const denominator = pt.total_payments_midpoint || pt.total_payments || 0;
      if (denominator > 0) {
        pt.implied_tr = (pt.total_fees / denominator) * 100;
      }
      if (pt.total_fees > 0) {
        pt.gpm = (pt.net_revenue / pt.total_fees) * 100;
      }
    });
    
    aggregatedData = {
      payment_types: aggregatedPaymentTypes,
      success: true
    };
  }

  if (!aggregatedData) {
    return null;
  }

  const payment_types = aggregatedData.payment_types || {};
  
  // Extract data from profitability result
  const nonAmexCC = payment_types.CC || {};
  const amex = payment_types.AMEX || {};
  const debit = payment_types.DEBIT || {};
  const ach = payment_types.ACH || {};

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return '0.00%';
    return `${value.toFixed(2)}%`;
  };

  // Calculate CC-based totals (Non-Amex CC + Amex + Debit)
  const ccBasedTotals = {
    gross_revenue: (nonAmexCC.total_fees || 0) + (amex.total_fees || 0) + (debit.total_fees || 0),
    merchant_fees: (nonAmexCC.merchant_fees || 0) + (amex.merchant_fees || 0) + (debit.merchant_fees || 0),
    net_revenue: (nonAmexCC.net_revenue || 0) + (amex.net_revenue || 0) + (debit.net_revenue || 0),
    collections: (nonAmexCC.total_collections || 0) + (amex.total_collections || 0) + (debit.total_collections || 0),
    transactions: (nonAmexCC.total_transactions || 0) + (amex.total_transactions || 0) + (debit.total_transactions || 0),
  };

  // Calculate metrics
  ccBasedTotals.gpm = ccBasedTotals.gross_revenue > 0 
    ? (ccBasedTotals.net_revenue / ccBasedTotals.gross_revenue * 100) 
    : 0;
  ccBasedTotals.implied_take_rate = ccBasedTotals.collections > 0 
    ? (ccBasedTotals.gross_revenue / ccBasedTotals.collections * 100) 
    : 0;
  ccBasedTotals.implied_mf = ccBasedTotals.gross_revenue > 0 
    ? (ccBasedTotals.merchant_fees / ccBasedTotals.gross_revenue * 100) 
    : 0;

  // ACH totals
  const achTotals = {
    gross_revenue: ach.total_fees || 0,
    merchant_fees: ach.merchant_fees || 0,
    net_revenue: ach.net_revenue || 0,
    collections: ach.total_collections || 0,
    transactions: ach.total_transactions || 0,
  };

  achTotals.gpm = achTotals.gross_revenue > 0 
    ? (achTotals.net_revenue / achTotals.gross_revenue * 100) 
    : 0;
  achTotals.implied_take_rate = achTotals.collections > 0 
    ? (achTotals.gross_revenue / achTotals.collections * 100) 
    : 0;
  achTotals.implied_mf = achTotals.gross_revenue > 0 
    ? (achTotals.merchant_fees / achTotals.gross_revenue * 100) 
    : 0;

  // Combined totals (ALL revenue and ALL fees - includes both CC and ACH)
  const combinedTotals = {
    gross_revenue: ccBasedTotals.gross_revenue + achTotals.gross_revenue,
    merchant_fees_all: ccBasedTotals.merchant_fees + achTotals.merchant_fees, // All fees (CC + ACH)
    net_revenue: ccBasedTotals.net_revenue + achTotals.net_revenue,
    collections: ccBasedTotals.collections + achTotals.collections,
    transactions: ccBasedTotals.transactions + achTotals.transactions,
  };

  // Combined metrics - use ALL fees (CC + ACH) for accurate combined view
  combinedTotals.gpm = combinedTotals.gross_revenue > 0 
    ? ((combinedTotals.gross_revenue - combinedTotals.merchant_fees_all) / combinedTotals.gross_revenue * 100) 
    : 0;
  combinedTotals.implied_take_rate = combinedTotals.collections > 0 
    ? (combinedTotals.gross_revenue / combinedTotals.collections * 100) 
    : 0;
  combinedTotals.implied_mf = combinedTotals.gross_revenue > 0 
    ? (combinedTotals.merchant_fees_all / combinedTotals.gross_revenue * 100) 
    : 0;

  // Helper to calculate metrics for individual payment types
  const calcMetrics = (paymentType) => {
    const data = payment_types[paymentType] || {};
    const gross = data.total_fees || 0;
    const merchant = data.merchant_fees || 0;
    const collections = data.total_collections || 0;
    
    return {
      gross_revenue: gross,
      merchant_fees: merchant,
      net_revenue: data.net_revenue || 0,
      gpm: data.gpm || 0,  // Use the pre-calculated GPM from backend
      implied_take_rate: data.implied_tr || 0,  // Use pre-calculated implied take rate
      implied_mf: data.implied_mf || 0,  // Use pre-calculated implied merchant fee %
      collections: collections,
      transactions: data.total_transactions || 0,
    };
  };

  const DetailedTableCard = ({ title, icon: Icon, iconColor, description, children }) => (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${iconColor} bg-opacity-10 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${iconColor.replace('bg-', 'text-')}`} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-500">{description}</p>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        {children}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Profitability by Payment Method</h2>
          <p className="text-xs text-slate-600 mt-1">Detailed breakdown by payment type</p>
        </div>
      </div>

      {/* Stacked layout - one table per card */}
      <div className="space-y-4">
        {/* Credit Card Based Payments - Detailed Breakdown */}
        <DetailedTableCard
          title="Credit Card Payments"
          description="Non-Amex, Debit, Amex"
          icon={CreditCard}
          iconColor="bg-purple-500"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left p-2 font-semibold">Type</th>
                  <th className="text-right p-2 font-semibold">Revenue</th>
                  <th className="text-right p-2 font-semibold">Merch. Fee</th>
                  <th className="text-right p-2 font-semibold">Net Rev.</th>
                  <th className="text-right p-2 font-semibold">GPM</th>
                  <th className="text-right p-2 font-semibold">TR%</th>
                </tr>
              </thead>
              <tbody>
                {/* Non-Amex CC */}
                <tr className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="p-2 font-medium text-purple-700">Non-Amex</td>
                  <td className="text-right p-2 text-xs">{formatCurrency(calcMetrics('CC').gross_revenue)}</td>
                  <td className="text-right p-2 text-xs">{formatCurrency(calcMetrics('CC').merchant_fees)}</td>
                  <td className="text-right p-2 font-semibold text-green-700 text-xs">{formatCurrency(calcMetrics('CC').net_revenue)}</td>
                  <td className="text-right p-2 font-semibold text-xs">{formatPercent(calcMetrics('CC').gpm)}</td>
                  <td className="text-right p-2 text-xs">{formatPercent(calcMetrics('CC').implied_take_rate)}</td>
                </tr>
                
                {/* Debit */}
                <tr className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="p-2 font-medium text-purple-700">Debit</td>
                  <td className="text-right p-2 text-xs">{formatCurrency(calcMetrics('DEBIT').gross_revenue)}</td>
                  <td className="text-right p-2 text-xs">{formatCurrency(calcMetrics('DEBIT').merchant_fees)}</td>
                  <td className="text-right p-2 font-semibold text-green-700 text-xs">{formatCurrency(calcMetrics('DEBIT').net_revenue)}</td>
                  <td className="text-right p-2 font-semibold text-xs">{formatPercent(calcMetrics('DEBIT').gpm)}</td>
                  <td className="text-right p-2 text-xs">{formatPercent(calcMetrics('DEBIT').implied_take_rate)}</td>
                </tr>

                {/* Amex */}
                <tr className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="p-2 font-medium text-purple-700">Amex</td>
                  <td className="text-right p-2 text-xs">{formatCurrency(calcMetrics('AMEX').gross_revenue)}</td>
                  <td className="text-right p-2 text-xs">{formatCurrency(calcMetrics('AMEX').merchant_fees)}</td>
                  <td className="text-right p-2 font-semibold text-green-700 text-xs">{formatCurrency(calcMetrics('AMEX').net_revenue)}</td>
                  <td className="text-right p-2 font-semibold text-xs">{formatPercent(calcMetrics('AMEX').gpm)}</td>
                  <td className="text-right p-2 text-xs">{formatPercent(calcMetrics('AMEX').implied_take_rate)}</td>
                </tr>

                {/* CC Total */}
                <tr className="bg-purple-50 border-t-2 border-purple-300 font-bold">
                  <td className="p-2 text-purple-900">CC Total</td>
                  <td className="text-right p-2 text-xs">{formatCurrency(ccBasedTotals.gross_revenue)}</td>
                  <td className="text-right p-2 text-xs">{formatCurrency(ccBasedTotals.merchant_fees)}</td>
                  <td className="text-right p-2 text-green-700 text-xs">{formatCurrency(ccBasedTotals.net_revenue)}</td>
                  <td className="text-right p-2 text-xs">{formatPercent(ccBasedTotals.gpm)}</td>
                  <td className="text-right p-2 text-xs">{formatPercent(ccBasedTotals.implied_take_rate)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-3 grid grid-cols-2 gap-3 pt-3 border-t border-slate-200 text-xs">
            <div>
              <span className="text-slate-500">Collections:</span>
              <span className="ml-2 font-semibold text-slate-900">{formatCurrency(ccBasedTotals.collections)}</span>
            </div>
            <div>
              <span className="text-slate-500">Transactions:</span>
              <span className="ml-2 font-semibold text-slate-900">{Math.round(ccBasedTotals.transactions).toLocaleString()}</span>
            </div>
          </div>
        </DetailedTableCard>

        {/* ACH Payments */}
        <DetailedTableCard
          title="ACH Payments"
          description="Bank transfers - high margin"
          icon={Landmark}
          iconColor="bg-blue-500"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left p-2 font-semibold">Type</th>
                  <th className="text-right p-2 font-semibold">Revenue</th>
                  <th className="text-right p-2 font-semibold">Merch. Fee</th>
                  <th className="text-right p-2 font-semibold">Net Rev.</th>
                  <th className="text-right p-2 font-semibold">GPM</th>
                  <th className="text-right p-2 font-semibold">TR%</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-blue-50 font-semibold">
                  <td className="p-2 text-blue-900">ACH</td>
                  <td className="text-right p-2 text-xs">{formatCurrency(achTotals.gross_revenue)}</td>
                  <td className="text-right p-2 text-xs">{formatCurrency(achTotals.merchant_fees)}</td>
                  <td className="text-right p-2 text-green-700 text-xs">{formatCurrency(achTotals.net_revenue)}</td>
                  <td className="text-right p-2 text-xs">{formatPercent(achTotals.gpm)}</td>
                  <td className="text-right p-2 text-xs">{formatPercent(achTotals.implied_take_rate)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-3 grid grid-cols-2 gap-3 pt-3 border-t border-slate-200 text-xs">
            <div>
              <span className="text-slate-500">Collections:</span>
              <span className="ml-2 font-semibold text-slate-900">{formatCurrency(achTotals.collections)}</span>
            </div>
            <div>
              <span className="text-slate-500">Transactions:</span>
              <span className="ml-2 font-semibold text-slate-900">{Math.round(achTotals.transactions).toLocaleString()}</span>
            </div>
          </div>
        </DetailedTableCard>

        {/* Combined Total */}
        <DetailedTableCard
          title="Combined Total"
          description="All payment types - CC + ACH combined view"
          icon={PieChart}
          iconColor="bg-emerald-500"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left p-2 font-semibold">Category</th>
                  <th className="text-right p-2 font-semibold">Revenue</th>
                  <th className="text-right p-2 font-semibold">Merch. Fee</th>
                  <th className="text-right p-2 font-semibold">Net Rev.</th>
                  <th className="text-right p-2 font-semibold">GPM</th>
                  <th className="text-right p-2 font-semibold">TR%</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-emerald-50 font-bold">
                  <td className="p-2 text-emerald-900">Combined</td>
                  <td className="text-right p-2 text-xs">{formatCurrency(combinedTotals.gross_revenue)}</td>
                  <td className="text-right p-2 text-xs">
                    {formatCurrency(combinedTotals.merchant_fees_all)}
                    <span className="text-[9px] text-emerald-700 block font-normal">{formatPercent(combinedTotals.implied_mf)}</span>
                  </td>
                  <td className="text-right p-2 text-green-700 text-xs">{formatCurrency(combinedTotals.net_revenue)}</td>
                  <td className="text-right p-2 text-xs">{formatPercent(combinedTotals.gpm)}</td>
                  <td className="text-right p-2 text-xs">{formatPercent(combinedTotals.implied_take_rate)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-3 grid grid-cols-2 gap-3 pt-3 border-t border-slate-200 text-xs">
            <div>
              <span className="text-slate-500">Collections:</span>
              <span className="ml-2 font-semibold text-slate-900">{formatCurrency(combinedTotals.collections)}</span>
            </div>
            <div>
              <span className="text-slate-500">Transactions:</span>
              <span className="ml-2 font-semibold text-slate-900">{Math.round(combinedTotals.transactions).toLocaleString()}</span>
            </div>
          </div>

          {/* Clear explanation */}
          <div className="mt-3 p-3 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg">
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-[10px] text-emerald-900 leading-relaxed">
                  <strong>Combined View:</strong> Includes all fees (CC + ACH) for accurate total margins. 
                  Sales commissions are calculated separately using CC revenue only per business rules.
                </p>
              </div>
            </div>
          </div>
        </DetailedTableCard>
      </div>

      {/* Explanation Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex gap-2">
          <div className="text-blue-600 mt-0.5">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="text-xs font-semibold text-blue-900 mb-1">Payment Type Breakdown</h4>
            <ul className="text-[10px] text-blue-800 space-y-0.5">
              <li>• <strong>CC Table:</strong> Credit card payments (Non-Amex, Debit, Amex) with typical processing fees</li>
              <li>• <strong>ACH Table:</strong> Bank transfers with significantly higher margins due to lower processing costs</li>
              <li>• <strong>Combined Table:</strong> Total across all payment types showing true blended margins</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
